import React, { useCallback, useMemo, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import {
  downloadMyPayrollSlipPdf,
  getMyPayrollSlips,
} from "features/Payroll/services/PayrollService";
import type { PayrollSlip } from "features/Payroll/types/payroll.types";
import StatusBadge from "components/common/StatusBadge";
import { payrollRunStatusMap } from "features/Payroll/constants/payroll.constants";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { formatCurrency } from "utils/currency";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

function formatMonthYear(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

async function triggerPdfDownload(slip: PayrollSlip): Promise<void> {
  const blob = await downloadMyPayrollSlipPdf(slip.id);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payslip-${slip.id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const EmployeePayslipsTab = () => {
  const now = new Date();
  const [month, setMonth] = useState<number | undefined>(now.getMonth() + 1);
  const [year, setYear] = useState<number | undefined>(now.getFullYear());
  const [downloading, setDownloading] = useState<number | null>(null);

  const handleDownload = useCallback(async (slip: PayrollSlip) => {
    setDownloading(slip.id);
    try {
      await triggerPdfDownload(slip);
    } finally {
      setDownloading(null);
    }
  }, []);

  const fetchApi = useCallback(
    async ({ page, limit }: { page: number; limit: number }) => {
      const res = await getMyPayrollSlips({ page, limit, month, year });
      return {
        success: res.success,
        message: res.message,
        data: res.data ?? {
          rows: [] as PayrollSlip[],
          totalItems: 0,
          currentPage: page,
          pageSize: limit,
          limit,
          totalPages: 0,
        },
      };
    },
    [month, year],
  );

  const columns = useMemo<ColumnDef<PayrollSlip>[]>(
    () => [
      IndexColumn<PayrollSlip>(),
      {
        header: "Period",
        id: "period",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { month, year } = row.original;
          return (
            <span className="fw-medium">{formatMonthYear(month, year)}</span>
          );
        },
      },
      {
        header: "Payroll Run",
        id: "payrollRun",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const run = row.original.payrollItem?.payrollRun;
          if (!run) return <span className="text-muted">—</span>;
          return (
            <Link
              to={`/finance/payroll/${run.id}`}
              className="fw-medium text-body"
            >
              {run.code ?? `#${run.id}`}
            </Link>
          );
        },
      },
      {
        header: "Gross",
        id: "gross",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span>{formatCurrency(row.original.payrollItem?.grossAmount)}</span>
        ),
      },
      {
        header: "Net Pay",
        id: "net",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium text-success">
            {formatCurrency(row.original.payrollItem?.netAmount)}
          </span>
        ),
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const s = row.original.payrollItem?.status;
          if (!s) return <span className="text-muted">—</span>;
          return (
            <StatusBadge value={s.toLowerCase()} map={payrollRunStatusMap} />
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const slip = row.original;
          const busy = downloading === slip.id;
          return (
            <Button
              size="sm"
              color="soft-primary"
              disabled={busy}
              onClick={() => handleDownload(slip)}
              title="Download payslip PDF"
            >
              {busy ? (
                <Spinner size="sm" />
              ) : (
                <i className="ri-download-2-line align-middle" />
              )}
            </Button>
          );
        },
      },
    ],
    [downloading, handleDownload],
  );

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-3">
        <h5 className="card-title mb-0">Payslips</h5>
        <div className="d-flex align-items-end gap-3">
          <div>
            <Label className="form-label mb-1">Month</Label>
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 140 }}
              value={month ?? ""}
              onChange={(e) =>
                setMonth(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">All months</option>
              {MONTHS.map((name, i) => (
                <option key={i + 1} value={i + 1}>
                  {name}
                </option>
              ))}
            </Input>
          </div>
          <div>
            <Label className="form-label mb-1">Year</Label>
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 110 }}
              value={year ?? ""}
              onChange={(e) =>
                setYear(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">All years</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Input>
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <TableContainer<PayrollSlip>
          columns={columns}
          fetchApi={fetchApi}
          pageSize={10}
          globalSearch={false}
          syncPaginationToUrl={false}
          divClass="table-responsive table-card mb-0"
          tableClass="align-middle table-nowrap mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
};

export default EmployeePayslipsTab;
